import type { EnumValueMeta } from '@joyful/utils'
import { areArraysEqual, createEnumSchema } from '@joyful/utils'
import type { SingleParser, SingleParserBuilder } from 'nuqs'
import { createParser } from 'nuqs'

export type ArrayFilterOperator = 'all' | 'any' | 'none' | 'notAll'

interface ArrayFilterOperatorMetadata extends EnumValueMeta {
  singleValueTitle?: string
}

export const ArrayFilterOperatorSchema = createEnumSchema<
  ArrayFilterOperator,
  ArrayFilterOperatorMetadata
>([
  {
    singleValueTitle: 'include',
    title: 'include all of',
    value: 'all',
  },
  {
    title: 'include any of',
    value: 'any',
  },
  {
    singleValueTitle: 'do not include',
    title: 'exclude if any of',
    value: 'none',
  },
  {
    title: 'exclude if all',
    value: 'notAll',
  },
])

export interface OperatorArrayFilter<ItemType> {
  operator: ArrayFilterOperator
  values: Array<ItemType>
}

export function parseAsOperatorArrayOf<ItemType>(
  itemParser: SingleParser<ItemType>,
  separator = ',',
): SingleParserBuilder<OperatorArrayFilter<ItemType>> {
  return createParser({
    eq: (a, b) => {
      return a.operator === b.operator && areArraysEqual(a.values, b.values)
    },

    parse: (value) => {
      if (!value) {
        return null
      }

      let operator: ArrayFilterOperator = 'all'
      let valuesStr = value

      // check for operator prefix
      const separatorIndex = value.indexOf(':')
      if (separatorIndex !== -1) {
        const potentialOperator = value.slice(0, separatorIndex)
        const parsed =
          ArrayFilterOperatorSchema.schema.safeParse(potentialOperator)
        if (parsed.success) {
          operator = parsed.data
          valuesStr = value.slice(separatorIndex + 1)
        }
      }

      // parse individual values
      const rawValues = valuesStr.split(separator).filter(Boolean)
      const parsedValues: Array<ItemType> = []

      for (const rawValue of rawValues) {
        const parsed = itemParser.parse(rawValue.trim())
        if (parsed !== null) {
          parsedValues.push(parsed)
        }
      }

      if (parsedValues.length === 0) {
        return null
      }

      return {
        operator,
        values: parsedValues,
      }
    },

    serialize: (filter) => {
      if (filter.values.length === 0) {
        return ''
      }

      const serializedValues = filter.values
        .map((v) => {
          return itemParser.serialize?.(v) ?? String(v)
        })
        .join(separator)

      return [filter.operator !== 'all' && filter.operator, serializedValues]
        .filter(Boolean)
        .join(':')
    },
  })
}

export function createOperatorArrayDefault<
  ItemType,
>(): OperatorArrayFilter<ItemType> {
  return {
    operator: 'all',
    values: [],
  }
}
